import useGetToken from 'src/firebase/useGetToken';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { StorageUtils, textFormatter } from 'src/utils';
import {
  assignConfirmModalId,
  assignModalId,
  assignSubMenuModalId,
  resetConfirmModalId,
  toggleNavBar
} from 'src/store/common/common.slice';
import { useGetIdentity, useSignOut } from 'src/hooks';
import { hTractionLogoWithText } from 'src/assets/images';
import { socketService } from 'src/services/socket.api.service';
import { setNotificationCount } from 'src/store/chat/chat.slice';
import { getCompany } from 'src/store/company/company.slice';
import { Button, Dropdown, ProfilePicture, SubMenuModal, TDropdown, Typography } from 'src/components/common';
import { inviteUserModalId } from 'src/components/cards/inviteNetwork';
import { SearchBar } from 'src/components/search';
import { HamburgerIcon, NotificationIcon, PlusIcon, SingleChevronIcon } from 'src/components/common/common.icons';
import { SocketAddress } from 'net';

import InviteUser from './inviteUser/InviteUser';
import './header.scss';

const signOutModalId = 'sign-up-modal-id';

const Header = () => {
  const { profile } = useAppSelector((store) => store.profile);
  const { companyProfilePic, myCompany } = useAppSelector((store) => store.company);
  const { dropdownId, showNavBar } = useAppSelector((store) => store.common);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthorized } = useAppSelector((store) => store.auth);
  const getTokenFunc = useGetToken();
    // Call get new token API when isAuthorized becomes false
    useEffect(() => {
      if (isAuthorized === false) {
        getTokenFunc();
      }
    }, [isAuthorized]);
  // Poll credits every 3 seconds
  useEffect(() => {
    if (!isAuthorized || !myCompany?.id) return;
    const interval = setInterval(() => {
      dispatch(
        // Import getCompany at the top and use it directly
        getCompany({ id: myCompany.id })
      );
    }, 6000);
    return () => clearInterval(interval);
  }, [isAuthorized, myCompany?.id, dispatch, myCompany?.id]);

  const { highlightNotificationBell, ideaHubNotificationCount } = useAppSelector(
    (store) => store.notifications
  );
  const userRole = profile?.role;

  // Constants
  const hasNotifications = ideaHubNotificationCount > 0 || highlightNotificationBell;

  const { notificationCount } = useAppSelector((store) => store.chat);
  const { getIdentities } = useGetIdentity();

  // state
  const menuRef = useRef(null);
  const openSignOutModal = () => {
    dispatch(assignConfirmModalId(signOutModalId));
  };

  const handleNavBar = () => {
    dispatch(toggleNavBar());
  };

  const isCompanyCreated = Boolean(StorageUtils.get('companyId'));
  const { loggedInUserType, authenticatedId } = getIdentities();
  const headerProfilePic = loggedInUserType === 'User' ? companyProfilePic : profile?.profilePic;
  const headerName = loggedInUserType === 'User' ? profile?.name : myCompany.name;
  const includedPath = ['/demo-day', '/demo-day-register', '/demo-day-register/payment', '/demo-day-register/document-submission', '/demo-day-register/slot-booking', '/demo-day-register/success', '/demo-day-detail', '/not-found']

  const navigateCompany = () => {
    if (isCompanyCreated) {
      navigate(`/my-company?tab=about&prev=${window.location.pathname}`);
    } else {
      navigate(`/create-company?prev=${window.location.pathname}`);
    }
  };

  const openNotifications = () => {
    navigate('/notifications?tab=all');
    dispatch(setNotificationCount(0));
  };

  const navigateToSettings = () => {
    navigate('/settings');
  };
  const navigateToDashboard = () => {
    const path = isAuthorized ? '/score' : '/';
    navigate(path)
  }

  const headerDropdownItems = useMemo(() => {
    const items = [
      // {
      //   label: isCompanyCreated ? 'Company Profile' : 'Create Company Page',
      //   onClick: navigateCompany
      // },

      { label: 'Settings', onClick: navigateToSettings },
      { label: 'Logout', onClick: openSignOutModal }
    ];

    // if (loggedInUserType === 'User') {
    //   items.unshift({ label: 'My Profile', onClick: () => navigate('/startup-onboarding') });
    // }

    return items;
  }, [isCompanyCreated]);

  const headerOptions: TDropdown = {
    id: 'header-dropdown',
    size: 'large',
    alignment: 'left',
    items: headerDropdownItems
  };
  const openDropdown = () => {
    if (dropdownId) {
      dispatch(assignSubMenuModalId(''));
    } else {
      dispatch(assignSubMenuModalId(headerOptions.id));
    }
  };

  const handleOpenInviteUserModal = () => {
    dispatch(assignModalId(inviteUserModalId));
  };
  // Detect Click Outside & Close Navbar
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!showNavBar || !menuRef.current) return;

      const navigationMenu = document.querySelector('.navigation__menu'); // Selects the menu
      // Check if the click is inside menuRef or navigation__menu
      if (
        menuRef.current.contains(event.target) ||
        navigationMenu?.contains(event.target)
      ) {
        return; // Do nothing if clicking inside
      }

      handleNavBar(); // Close sidebar when clicking outside
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNavBar]);

  return (
    <div className="demo-day-header-component" >
      <div className="demo-day-header-component__wrapper">
        <img
          src={hTractionLogoWithText}
          alt="htraction logo"
          className="demo-day-header-component__htraction-logo"
          onClick={navigateToDashboard}
        />
        {isAuthorized && <div className="demo-day-header-component__search">
          {/* <SearchBar /> */}
        </div>}
        {!isAuthorized && <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div className="demo-day-header-component__search" style={{ opacity: '0', width: '85%' }}></div>
          <Button
            variant="secondary"
            size="extraSmall"
            style={{ width: 'auto' }}
            className="demo-day-header-component__right__invite-user"
            onClick={() => navigate('/login')}
          >
            Login / Signup
          </Button>
        </div>}
        {isAuthorized && profile.role && userRole && window.innerWidth > 768 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div className="demo-day-header-component__search" style={{ opacity: '0', width: '100%' }}></div>
            {myCompany.credits > 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1.5px solid #FF7E1B',
                  borderRadius: 12,
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(255, 75, 38, 0.04)',
                  fontWeight: 500,
                  fontSize: 15,
                  minWidth: 240,
                  maxHeight: 44,
                  marginRight: '13px',
                  overflow: 'hidden'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px 0 16px', gap: 8, height: 44 }}>
                  🪙
                  <span style={{ color: '#FF7E1B', fontWeight: 700, fontSize: 12 }}>{myCompany.credits.toLocaleString('en-IN')}</span>
                  <span style={{ color: '#FF7E1B', fontWeight: 500, fontSize: 12, marginLeft: 2 }}>Credits</span>
                </div>
                <div style={{ width: 1, height: 28, background: '#E0E0E0', margin: '0 0px' }} />
                <span
                  onClick={() => navigate('/startup-payment')}
                  style={{ color: '#FF7E1B', fontWeight: 700, fontSize: 12, cursor: 'pointer', paddingLeft: '10px', userSelect: 'none', display: 'flex', alignItems: 'center', height: 44 }}
                >
                  Get Credits
                </span>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="extraSmall"
                style={{ width: 'auto', marginRight: '13px', height: 35, fontWeight: 700, fontSize: 12 }}
                onClick={() => navigate('/startup-payment')}
              >
                Add Credits
              </Button>
            )}
          </div>
        )}
        {isAuthorized && profile.role && userRole && window.innerWidth <= 768 && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', height: 65, gap: 16, width: '100%' }}>
            {myCompany.credits > 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1.5px solid #FF7E1B',
                  borderRadius: 28,
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(255, 75, 38, 0.04)',
                  fontWeight: 500,
                  fontSize: 17,
                  minWidth: 100,
                  height: 40,
                  padding: '0 18px',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/startup-payment')}
              >
                🪙
                <span style={{ color: '#FF7E1B', fontWeight: 700, fontSize: 15, marginLeft: 8 }}>{myCompany.credits.toLocaleString('en-IN')}</span>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="extraSmall"
                style={{ width: 'auto', height: 40, fontWeight: 700, fontSize: 14, borderRadius: 28, padding: '0 12px' }}
                onClick={() => navigate('/startup-payment')}
              >
                Add Credit
              </Button>
            )}
            <div className="demo-day-header-component__right__menu__profile-picture" style={{ marginLeft: 0, width: 56, height: 56, minWidth: 56, minHeight: 56, maxWidth: 56, maxHeight: 56, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => {
              e.stopPropagation();
              openDropdown();
            }}>
              <ProfilePicture
                url={headerProfilePic || ''}
                fallback={textFormatter({ value: headerName[0] || '' })}
                userType={loggedInUserType}
              />
              {/* Render dropdown for mobile */}
              {dropdownId === headerOptions.id && (
                <SubMenuModal>
                  <Dropdown {...headerOptions} />
                </SubMenuModal>
              )}
            </div>
          </div>
        )}
        {window.innerWidth > 768 && (
          <div className="demo-day-header-component__right">
            <div className="demo-day-header-component__right__menu" >
              {isAuthorized && (
                <div className="demo-day-header-component__right__menu__profile-picture" style={{ marginLeft: 'auto' }} onClick={(e) => {
                  e.stopPropagation();
                  openDropdown();
                }}>
                  <ProfilePicture
                    url={headerProfilePic || ''}
                    fallback={textFormatter({ value: headerName[0] || '' })}
                    userType={loggedInUserType}
                  />
                </div>
              )}
              <SubMenuModal>
                <Dropdown {...headerOptions} />
              </SubMenuModal>
            </div>
          </div>
        )}
        {<div className="demo-day-header-component__right">
          {/* pg */}
          {/* {isAuthorized && <div className="demo-day-header-component__right__notifications" onClick={openNotifications}>
            <div className="header-component__right__notifications__container">
              {notificationCount > 0 && (
                <div className="header-component__right__notifications__container__new-notification">
                  <span>{notificationCount > 99 ? `${99}+` : notificationCount}</span>
                </div>
              )}
              <NotificationIcon height='22px' width='24px' />
            </div>
          </div>} */}
        </div>}

        {/* {openInviteUserModal && isAuthorized && (
        <InviteUser isModalOpen={openInviteUserModal} closeModal={handleCloseInviteUserModal} />
      )} */}

      </div>
    </div >
  );
};

export default Header;
